import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import Markdown from 'react-markdown'
import { Lock, Plus, X, ArrowUp, ArrowDown, Copy, ExternalLink, Check, Code2 } from 'lucide-react'
import { adminApi, type CourseWebsiteDto, type CourseWebsiteSave } from '@/api/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import WebsiteImages from './WebsiteImages'
import CourseEmbedModal from './CourseEmbedModal'
import { categoryErrorMessage } from './categoryErrors'
import UnsavedBar from '@/components/shared/UnsavedBar'

const MAX_OVERVIEW = 5000
const MAX_WHO = 500
const MAX_ITEMS = 12
const MAX_ITEM_LENGTH = 200

/** A list of short items: type one and press Enter, remove or reorder them. */
function BulletListEditor({ id, label, hint, items, onChange }: {
  id: string
  label: string
  hint: string
  items: string[]
  onChange: (items: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  const full = items.length >= MAX_ITEMS

  const add = () => {
    const text = draft.trim()
    if (!text || full) return
    onChange([...items, text])
    setDraft('')
  }
  const move = (index: number, dir: -1 | 1) => {
    const next = [...items]
    const target = index + dir
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  return (
    <div className="space-y-2">
      <div>
        <Label htmlFor={id}>{label}</Label>
        <p className="text-xs text-gray-500">{hint}</p>
      </div>
      {items.length > 0 && (
        <ul className="space-y-1.5">
          {items.map((item, i) => (
            <li key={`${i}-${item}`} className="flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2 py-1.5">
              <span className="min-w-0 flex-1 break-words text-sm text-gray-800">{item}</span>
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move "${item}" up`}
                className="text-gray-400 hover:text-gray-700 disabled:opacity-30 cursor-pointer disabled:cursor-default"><ArrowUp size={14} /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} aria-label={`Move "${item}" down`}
                className="text-gray-400 hover:text-gray-700 disabled:opacity-30 cursor-pointer disabled:cursor-default"><ArrowDown size={14} /></button>
              <button type="button" onClick={() => onChange(items.filter((_, x) => x !== i))} aria-label={`Remove "${item}"`}
                className="text-gray-400 hover:text-red-600 cursor-pointer"><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2">
        <Input
          id={id}
          value={draft}
          maxLength={MAX_ITEM_LENGTH}
          disabled={full}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          placeholder={full ? `Up to ${MAX_ITEMS} items` : 'Type an item and press Enter'}
        />
        <Button type="button" size="sm" variant="outline" className="gap-1" onClick={add} disabled={!draft.trim() || full}>
          <Plus size={14} /> Add
        </Button>
      </div>
    </div>
  )
}

/** The overview is written as markdown; this shows how it will read before it is saved. */
function OverviewField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [preview, setPreview] = useState(false)
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor="website-overview">Overview</Label>
        <div role="tablist" aria-label="Overview mode" className="inline-flex rounded-md bg-gray-100 p-0.5 text-xs">
          {(['Write', 'Preview'] as const).map(mode => (
            <button
              key={mode}
              type="button"
              role="tab"
              aria-selected={preview === (mode === 'Preview')}
              onClick={() => setPreview(mode === 'Preview')}
              className={`rounded px-2.5 py-1 font-medium cursor-pointer ${preview === (mode === 'Preview') ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
            >{mode}</button>
          ))}
        </div>
      </div>
      {preview ? (
        <div className="min-h-[9rem] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 [&_a]:text-indigo-600 [&_a]:underline [&_h2]:mt-3 [&_h2]:text-base [&_h2]:font-semibold [&_h3]:mt-2 [&_h3]:font-semibold [&_li]:ml-4 [&_ol]:list-decimal [&_p]:mb-2 [&_ul]:list-disc">
          {value.trim() ? <Markdown>{value}</Markdown> : <p className="text-gray-400">Nothing to preview yet.</p>}
        </div>
      ) : (
        <textarea
          id="website-overview"
          value={value}
          maxLength={MAX_OVERVIEW}
          rows={7}
          onChange={e => onChange(e.target.value)}
          placeholder="A short description of the course. You can use **bold**, *italic*, lists and [links](https://example.com)."
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      )}
      <p className="text-right text-xs text-gray-400">{value.length}/{MAX_OVERVIEW}</p>
    </div>
  )
}

/** The address of the page CertSlate hosts for this course, once it is switched on. */
function HostedPageAddress({ tenantSlug, courseSlug, ownPageUrl }: { tenantSlug: string; courseSlug: string; ownPageUrl: string | null }) {
  const [copied, setCopied] = useState(false)
  const [showEmbed, setShowEmbed] = useState(false)
  const url = `${window.location.origin}/courses/${tenantSlug}/${courseSlug}`
  const catalogue = `${window.location.origin}/courses/${tenantSlug}`

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch { /* the address is shown, so it can be copied by hand */ }
  }

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Your course page</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">CertSlate hosts a page for this course. Link to it from your website, or share it directly.</p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 break-all rounded-md bg-gray-100 px-3 py-2 text-xs text-gray-800" data-testid="hosted-page-url">{url}</code>
          <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={copy}>
            {copied ? <Check size={14} /> : <Copy size={14} />}{copied ? 'Copied' : 'Copy'}
          </Button>
          <a href={url} target="_blank" rel="noopener noreferrer"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <ExternalLink size={14} /> View page
          </a>
          <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setShowEmbed(true)}>
            <Code2 size={14} /> Embed
          </Button>
        </div>
        {showEmbed && <CourseEmbedModal initialCourseSlug={courseSlug} onClose={() => setShowEmbed(false)} />}
        <p className="text-xs text-gray-500">All your courses: <a href={catalogue} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">{catalogue}</a></p>
        {ownPageUrl ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This course already has its own page ({ownPageUrl}), so search engines are asked not to list this one. That avoids two competing pages. Clear the course's website address on the Details tab if you would rather use this page.
          </p>
        ) : (
          <p className="text-xs text-gray-500">Search engines can list this page.</p>
        )}
      </CardContent>
    </Card>
  )
}

function toWebsiteForm(data: CourseWebsiteDto): CourseWebsiteSave {
  return {
    showOnWebsite: data.showOnWebsite,
    slug: data.slug ?? data.suggestedSlug,
    overview: data.overview ?? '',
    whoItsFor: data.whoItsFor ?? '',
    whatYoullLearn: data.whatYoullLearn,
    requirements: data.requirements,
    whatsIncluded: data.whatsIncluded,
  }
}

function WebsiteForm({ data, tenantSlug, ownPageUrl, onOpenDetails, onDirtyChange }: {
  data: CourseWebsiteDto
  tenantSlug: string | null
  ownPageUrl: string | null
  onOpenDetails?: () => void
  onDirtyChange?: (dirty: boolean) => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<CourseWebsiteSave>(() => toWebsiteForm(data))
  const [saved, setSaved] = useState(false)

  // Unsaved when the form differs from what is saved. Told to the page, so leaving the tab can warn.
  const dirty = JSON.stringify(form) !== JSON.stringify(toWebsiteForm(data))
  useEffect(() => {
    onDirtyChange?.(dirty)
    return () => onDirtyChange?.(false)
  }, [dirty, onDirtyChange])
  const set = <K extends keyof CourseWebsiteSave>(key: K, value: CourseWebsiteSave[K]) => {
    setForm(f => ({ ...f, [key]: value }))
    setSaved(false)
  }

  const save = useMutation({
    mutationFn: () => adminApi.updateCourseWebsite(data.courseId, {
      ...form,
      slug: form.slug?.trim() || null,
      overview: form.overview?.trim() || null,
      whoItsFor: form.whoItsFor?.trim() || null,
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['course-website', data.courseId] })
      await queryClient.invalidateQueries({ queryKey: ['courses'] })
      // Show what the server actually kept (it trims text and drops blank or repeated list items), so nothing looks unsaved.
      const fresh = queryClient.getQueryData<CourseWebsiteDto>(['course-website', data.courseId])
      if (fresh) setForm(toWebsiteForm(fresh))
      setSaved(true)
      window.setTimeout(() => setSaved(false), 2000)
    },
    onError: () => setSaved(false),
  })

  const turningOn = form.showOnWebsite && !data.showOnWebsite && !data.slugLocked

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">On your website</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
              checked={form.showOnWebsite}
              onChange={e => set('showOnWebsite', e.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">Show this course on my website</span>
              <span className="block text-xs text-gray-500">Off by default, so internal or private courses never appear.</span>
            </span>
          </label>

          <div className="space-y-1.5">
            <Label htmlFor="website-slug">Web address</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">/courses/…/</span>
              <Input
                id="website-slug"
                value={form.slug ?? ''}
                maxLength={80}
                disabled={data.slugLocked}
                onChange={e => set('slug', e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                className="max-w-xs font-mono"
              />
              {data.slugLocked && <Lock size={14} className="text-gray-400" aria-label="Locked" />}
            </div>
            <p className="text-xs text-gray-500">
              {data.slugLocked
                ? 'Locked, because this course has been on your website and links to it must keep working. Contact support if it really needs to change.'
                : 'Lowercase letters, numbers and hyphens. It locks the first time the course is shown, so choose it now.'}
            </p>
            {turningOn && <p className="text-xs font-medium text-amber-600">Saving will lock this address.</p>}
          </div>

          <div className="space-y-1" data-testid="own-page-row">
            <p className="text-sm font-medium text-gray-900">Your own page for this course</p>
            {ownPageUrl ? (
              <p className="break-all text-sm text-gray-700">{ownPageUrl}</p>
            ) : (
              <p className="text-sm text-gray-500">None set. Search engines can list your CertSlate course page, and booking emails link to it.</p>
            )}
            <p className="text-xs text-gray-500">
              {ownPageUrl
                ? 'Booking emails and embedded course cards link there, and search engines are asked not to list the CertSlate page. '
                : 'If this course has a page on your own website, add it so booking emails and embedded cards can link there. '}
              {onOpenDetails && (
                <button type="button" onClick={onOpenDetails} className="font-medium text-indigo-600 underline cursor-pointer">
                  {ownPageUrl ? 'Change it' : 'Add it'} on the Details tab
                </button>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {data.showOnWebsite && data.slug && tenantSlug && (
        <HostedPageAddress tenantSlug={tenantSlug} courseSlug={data.slug} ownPageUrl={ownPageUrl} />
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Content</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <OverviewField value={form.overview ?? ''} onChange={v => set('overview', v)} />

          <div className="space-y-1.5">
            <Label htmlFor="website-who">Who it's for</Label>
            <textarea
              id="website-who"
              value={form.whoItsFor ?? ''}
              maxLength={MAX_WHO}
              rows={2}
              onChange={e => set('whoItsFor', e.target.value)}
              placeholder="e.g. Designated first aiders in low-risk workplaces"
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>

          <BulletListEditor id="website-learn" label="What you'll learn" hint="Short points, shown as a list."
            items={form.whatYoullLearn} onChange={v => set('whatYoullLearn', v)} />
          <BulletListEditor id="website-requirements" label="Requirements" hint="Anything people need before they book, such as age or prior certificates."
            items={form.requirements} onChange={v => set('requirements', v)} />
          <BulletListEditor id="website-included" label="What's included" hint="For example certificate, course materials, refreshments."
            items={form.whatsIncluded} onChange={v => set('whatsIncluded', v)} />

        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Pictures</CardTitle></CardHeader>
        <CardContent>
          <p className="mb-3 text-xs text-gray-500">The first picture is the main one. Pictures save as soon as you add or change them.</p>
          <WebsiteImages courseId={data.courseId} images={data.images} />
        </CardContent>
      </Card>

      <UnsavedBar
        visible={dirty}
        saved={saved}
        error={save.isError ? categoryErrorMessage(save.error, 'Could not save. Please try again.') : null}
        isPending={save.isPending}
        onSave={() => save.mutate()}
        onCancel={() => { setForm(toWebsiteForm(data)); save.reset() }}
      />
    </div>
  )
}

/** The Website tab on a course: what appears on the customer's own website. Pro plan only. */
export default function WebsiteTab({ courseId, onOpenDetails, onDirtyChange }: {
  courseId: string
  onOpenDetails?: () => void
  onDirtyChange?: (dirty: boolean) => void
}) {
  const [showPro, setShowPro] = useState(false)

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
  const isPro = profile?.planTier === 'Pro'

  const { data, isLoading } = useQuery({
    queryKey: ['course-website', courseId],
    queryFn: () => adminApi.getCourseWebsite(courseId).then(r => r.data),
    enabled: isPro,
  })

  // Same query as the Details tab, so it is normally already loaded.
  const { data: course } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => adminApi.getCourse(courseId).then(r => r.data),
    enabled: isPro,
  })

  if (profileLoading) return <p className="text-gray-500">Loading…</p>

  if (!isPro) {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-4 p-6 sm:flex-row sm:items-center">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50"><Lock size={18} className="text-amber-500" /></div>
          <div className="flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-600">Pro feature</p>
            <h2 className="text-base font-semibold text-gray-900">Course pages for your website</h2>
            <p className="mt-1 text-sm text-gray-500">
              Add a description, pictures and key details to each course and show them on your own website, ready for visitors to book.
            </p>
          </div>
          <Button type="button" onClick={() => setShowPro(true)}>See what Pro includes</Button>
          <ProUpgradeDialog open={showPro} onClose={() => setShowPro(false)} featureName="Course pages for your website" />
        </CardContent>
      </Card>
    )
  }

  if (isLoading || !data) return <p className="text-gray-500">Loading…</p>
  // Keyed on the course so a different course never inherits another's draft.
  return <WebsiteForm key={data.courseId} data={data} tenantSlug={profile?.slug ?? null} ownPageUrl={course?.websiteUrl?.trim() || null} onOpenDetails={onOpenDetails} onDirtyChange={onDirtyChange} />
}
